import log from "loglevel";
import { inject, injectable } from "tsyringe";
import { TOKEN_ISecureServerCalls, type ISecureServerCalls } from "./SecureServerCalls";
import { ProjectBusEvents, TOKEN_IProjectBus, type IProjectBus } from "./ProjectBus";
import { TOKEN_IEnvironment, type IEnvironment } from "./IEnvironment";
import { EventPlaneDBClient } from "@utilitydelta/eventplanedb-client";

export const TOKEN_IRealtime = Symbol('IRealtime');

export interface IRealtime {
    /**
     * Checks if there is an active connection for the specified project
     * @param projectId The ID of the project to check
     * @returns Promise that resolves to true if connected, false otherwise
     */
    isConnected(projectId: string): Promise<boolean>;

    /**
     * Disconnects from the specified project's realtime updates
     * @param projectId The ID of the project to disconnect from
     */
    disconnect(projectId: string): Promise<void>;

    /**
     * Disconnects from all active project connections.
     */
    disconnectAll(): Promise<void>;

    /**
     * Connects to a project's realtime updates
     * @param projectId The ID of the project to connect to
     * @param newEventsReceived Callback function that will be called when new events are received
     */
    connect(projectId: string, newEventsReceived: (projectId: string) => void): Promise<void>;
}

@injectable()
export class Realtime implements IRealtime {
    private _connections: Record<string, EventSource> = {};

    constructor(
        @inject(TOKEN_ISecureServerCalls) private secureServerCalls: ISecureServerCalls,
        @inject(TOKEN_IEnvironment) private environment: IEnvironment,
        @inject(TOKEN_IProjectBus) projectBus: IProjectBus,
    ) {
        projectBus.subscribe_tabFocusChanged(this.onTabFocusChanged);
    }

    private onTabFocusChanged = async (data: ProjectBusEvents['TAB_FOCUS_CHANGED']) => {
        if (!data.isFocused && this.environment.isProd) {
            await this.disconnectAll();
        }
    }

    private connectionFromProject = async (projectId: string): Promise<EventSource | undefined> => {
        const endpointUrl = await this.endpoint(projectId);
        return this._connections[endpointUrl];
    }

    isConnected = async (projectId: string): Promise<boolean> => {
        const connection = await this.connectionFromProject(projectId);
        return connection !== undefined;
    }

    disconnect = async (projectId: string) => {
        const connection = await this.connectionFromProject(projectId);
        if (connection === undefined) return;

        log.info("Leaving any existing connected project group");
        EventPlaneDBClient.closeRealtimeConnection(connection);

        const endpointUrl = await this.endpoint(projectId);
        delete this._connections[endpointUrl];
    }

    disconnectAll = async () => {
        log.info("Disconnecting from all active projects.");

        for (const baseUrl in this._connections) {
            const connection = this._connections[baseUrl];
            EventPlaneDBClient.closeRealtimeConnection(connection);
            delete this._connections[baseUrl];
        }
    }

    private async endpoint(projectId: string): Promise<string> {
        const base_url = await this.secureServerCalls.getRealtimeBaseUrl(projectId);
        return base_url + `/api/v1/aggregate/${projectId}/subscribe`;
    }

    connect = async (projectId: string, newEventsReceived: (projectId: string) => void) => {
        if (await this.isConnected(projectId)) return;

        const baseUrl = await this.secureServerCalls.getRealtimeBaseUrl(projectId);
        const authData = await this.secureServerCalls.AuthData();
        const endpointUrl = await this.endpoint(projectId);

        const messageHandler = (event: MessageEvent) => {
            if (event.data === 'ne') {
                newEventsReceived(projectId);
            }
        };

        const errorHandler = (error: Event) => {
            log.error('SSE connection error:', error);
        };

        const eventSource = EventPlaneDBClient.createRealtimeConnection(
            baseUrl,
            authData,
            projectId,
            messageHandler,
            errorHandler
        );

        this._connections[endpointUrl] = eventSource;
    }
}